import { ActionIcon, Avatar, Group, Menu, Text, UnstyledButton } from '@mantine/core';
import {
  IconBrain,
  IconBuildingSkyscraper,
  IconGitBranch,
  IconLogout,
  IconRobot,
  IconSettings,
  IconShieldCog,
  IconUsers,
} from '@tabler/icons-react';
import { Link } from '@tanstack/react-router';
import { useAuth } from '@/contexts/auth';
import { useMe } from '@/hooks/use-me';
import { isTemplatesEnabled } from '@/lib/features';

function initialsOf(firstName: string | null, lastName: string | null, email: string) {
  const fromName = [firstName?.[0] ?? '', lastName?.[0] ?? ''].filter(Boolean).join('').toUpperCase();

  return fromName || email.slice(0, 2).toUpperCase() || 'U';
}

function SystemAdminMenu() {
  return (
    <Menu.Sub>
      <Menu.Sub.Target>
        <Menu.Sub.Item leftSection={<IconShieldCog size={16} stroke={1.5} />}>System Admin</Menu.Sub.Item>
      </Menu.Sub.Target>
      <Menu.Sub.Dropdown>
        <Menu.Item component={Link} to="/system/super-users" leftSection={<IconUsers size={16} stroke={1.5} />}>
          Super Users
        </Menu.Item>
        <Menu.Item
          component={Link}
          to="/system/organizations"
          leftSection={<IconBuildingSkyscraper size={16} stroke={1.5} />}
        >
          Organizations
        </Menu.Item>
        <Menu.Item component={Link} to="/system/ai-models" leftSection={<IconBrain size={16} stroke={1.5} />}>
          AI Models
        </Menu.Item>
        {isTemplatesEnabled() && (
          <>
            <Menu.Item
              component={Link}
              to="/system/persona-templates"
              leftSection={<IconRobot size={16} stroke={1.5} />}
            >
              Persona Templates
            </Menu.Item>
            <Menu.Item
              component={Link}
              to="/system/flow-templates"
              leftSection={<IconGitBranch size={16} stroke={1.5} />}
            >
              Flow Templates
            </Menu.Item>
          </>
        )}
      </Menu.Sub.Dropdown>
    </Menu.Sub>
  );
}

export function UserMenu() {
  const { signOut } = useAuth();
  const { me, isSuperUser, displayName } = useMe();
  const initials = initialsOf(me.first_name, me.last_name, me.email);

  return (
    <Menu position="bottom-end" offset={8}>
      <Menu.Target>
        <UnstyledButton>
          <Avatar size={32} radius="md" color="indigo">
            {initials}
          </Avatar>
        </UnstyledButton>
      </Menu.Target>

      <Menu.Dropdown>
        <Menu.Label>
          <Group gap="xs" wrap="nowrap">
            <Avatar size={32} radius="md" color="indigo">
              {initials}
            </Avatar>
            <div style={{ flex: 1, minWidth: 0 }}>
              <Text size="sm" fw={500} truncate>
                {displayName}
              </Text>
              <Text size="xs" c="dimmed" truncate>
                {me.email}
              </Text>
            </div>
            <ActionIcon component={Link} to="/user" variant="subtle" color="gray" size="sm">
              <IconSettings size={16} stroke={1.5} />
            </ActionIcon>
          </Group>
        </Menu.Label>
        {isSuperUser && (
          <>
            <Menu.Divider />
            <SystemAdminMenu />
          </>
        )}
        <Menu.Divider />
        <Menu.Item color="red" leftSection={<IconLogout size={16} stroke={1.5} />} onClick={signOut}>
          Sign Out
        </Menu.Item>
      </Menu.Dropdown>
    </Menu>
  );
}
